import { useState, useEffect, useCallback } from 'react';
import { useCardForm } from 'src/hooks/useCardForm';
import { useTranslate } from 'src/locales/use-locales';
import type { PartographTraceData } from '../models';
import { referenceService } from 'src/api/references/referenceService';
import { getLocalISO, toLocalISO } from 'src/utils/date/dateHelper';

interface UsePartographFormProps {
  cardType: string;
  recordId?: string;
  user?: any;
  comId: number;
  onLoadRecord?: (record: any) => void;
}

export const usePartographForm = ({
  cardType,
  recordId,
  user,
  comId,
  onLoadRecord
}: UsePartographFormProps) => {
  const { t } = useTranslate();

  // Ana form için generic useCardForm hook'unu kullan
  const cardForm = useCardForm({
    cardType,
    recordId,
    user,
    onLoadRecord,
  });

  // Trace verileri
  const [traceData, setTraceData] = useState<PartographTraceData[]>([]);
  const [loadingTrace, setLoadingTrace] = useState(false);
  const [masterId, setMasterId] = useState<string | null>(null); // MC_PARTOGRAPH.ID (MP_ID)
  const [detId, setDetId] = useState<string | null>(null);       // MC_PARTOGRAPH_DET.ID (MPD_ID)
  const [detList, setDetList] = useState<any[]>([]);             // MC_PARTOGRAPH_DET rows for MP_ID
  const [loadingDetList, setLoadingDetList] = useState(false);

  // Grafik verileri
  const [fetalHeartRateData, setFetalHeartRateData] = useState<any[]>([]);
  const [serviksSizeData, setServiksSizeData] = useState<any[]>([]);
  const [headLevelData, setHeadLevelData] = useState<any[]>([]);
  const [painLevelData, setPainLevelData] = useState<any[]>([]);
  const [amnionFluidData, setAmnionFluidData] = useState<any[]>([]);

  // Initial loading state
  const [initialLoading, setInitialLoading] = useState(true);

  // Grafik verilerini hazırla (şimdilik frontend tarafında çalışıyor)
  const prepareChartData = useCallback((traces: any[]) => {
    // Fetal Heart Rate
    const fetalHeartRate = traces
      .filter(t => t.FETAL_HEART_RATE !== null && t.FETAL_HEART_RATE !== undefined)
      .map(t => ({
        PROCESS_TIME: t.PROCESS_TIME ? new Date(t.PROCESS_TIME) : null,
        FETAL_HEART_RATE: t.FETAL_HEART_RATE,
        MEDIAN_LINE_1: 120,
        MEDIAN_LINE_2: 160,
      }))
      .sort((a, b) => {
        if (!a.PROCESS_TIME || !b.PROCESS_TIME) return 0;
        return a.PROCESS_TIME.getTime() - b.PROCESS_TIME.getTime();
      });
    setFetalHeartRateData(fetalHeartRate);

    // Serviks Size
    const serviksSizeRaw = traces
      .filter(t => t.SERVIKS_SIZE !== null && t.SERVIKS_SIZE !== undefined)
      .sort((a, b) => {
        if (!a.PROCESS_TIME || !b.PROCESS_TIME) return 0;
        return new Date(a.PROCESS_TIME).getTime() - new Date(b.PROCESS_TIME).getTime();
      });

    // Find the first measurement where dilation is >= 4cm (Active Phase start)
    const activePhaseStart = serviksSizeRaw.find(t => Number(t.SERVIKS_SIZE) >= 4);
    const activeStartTime = activePhaseStart ? new Date(activePhaseStart.PROCESS_TIME).getTime() : null;

    const serviksSize = serviksSizeRaw.map(t => {
      const currentTime = new Date(t.PROCESS_TIME).getTime();
      let alertValue = null;
      let actionValue = null;

      if (activeStartTime) {
        // Alert Line: Starts at 4cm and increases 1cm per hour
        const hoursFromStart = (currentTime - activeStartTime) / (1000 * 60 * 60);
        alertValue = Math.min(10, 4 + hoursFromStart);

        // Action Line: Parallel to Alert Line, 4 hours later
        const fourHoursLaterTime = currentTime - (4 * 1000 * 60 * 60);
        const hoursFromStartForAction = (fourHoursLaterTime - activeStartTime) / (1000 * 60 * 60);
        if (hoursFromStartForAction >= 0) {
          actionValue = Math.min(10, 4 + hoursFromStartForAction);
        }
      }

      return {
        PROCESS_TIME: new Date(t.PROCESS_TIME),
        SERVIKS_SIZE: Number(t.SERVIKS_SIZE),
        WARNING_AREA: alertValue,
        INTERVENTION_AREA: actionValue,
        MEDIAN_LINE: 4,
      };
    });
    setServiksSizeData(serviksSize);

    // Head Level
    const headLevel = traces
      .filter(t => t.HEAD_LEVEL !== null && t.HEAD_LEVEL !== undefined)
      .map(t => ({
        PROCESS_TIME: t.PROCESS_TIME ? new Date(t.PROCESS_TIME) : null,
        HEAD_LEVEL: t.HEAD_LEVEL,
      }))
      .sort((a, b) => {
        if (!a.PROCESS_TIME || !b.PROCESS_TIME) return 0;
        return a.PROCESS_TIME.getTime() - b.PROCESS_TIME.getTime();
      });
    setHeadLevelData(headLevel);

    // Pain Level
    const painLevel = traces
      .filter(t => t.PAIN_LEVEL !== null && t.PAIN_LEVEL !== undefined)
      .map(t => ({
        PROCESS_TIME: t.PROCESS_TIME ? new Date(t.PROCESS_TIME) : null,
        PAIN_LEVEL: t.PAIN_LEVEL,
        PAIN_COUNT: t.PAIN_COUNT,
        PAIN_LEVEL_1: t.PAIN_COUNT >= 1 ? t.PAIN_LEVEL : 0,
        PAIN_LEVEL_2: t.PAIN_COUNT >= 2 ? t.PAIN_LEVEL : 0,
        PAIN_LEVEL_3: t.PAIN_COUNT >= 3 ? t.PAIN_LEVEL : 0,
        PAIN_LEVEL_4: t.PAIN_COUNT >= 4 ? t.PAIN_LEVEL : 0,
        PAIN_LEVEL_5: t.PAIN_COUNT === 5 ? t.PAIN_LEVEL : 0,
      }))
      .sort((a, b) => {
        if (!a.PROCESS_TIME || !b.PROCESS_TIME) return 0;
        return a.PROCESS_TIME.getTime() - b.PROCESS_TIME.getTime();
      });
    setPainLevelData(painLevel);

    // Amnion Fluid
    const amnionFluid = traces
      .filter(t => t.AMNION_FLUID !== null && t.AMNION_FLUID !== undefined)
      .map(t => ({
        PROCESS_TIME: t.PROCESS_TIME ? new Date(t.PROCESS_TIME) : null,
        AMNION_FLUID: t.AMNION_FLUID,
      }))
      .sort((a, b) => {
        if (!a.PROCESS_TIME || !b.PROCESS_TIME) return 0;
        return a.PROCESS_TIME.getTime() - b.PROCESS_TIME.getTime();
      });
    setAmnionFluidData(amnionFluid);
  }, []);

  // Şimdilik trace ve kayıt işlemleri için stub fonksiyonlar
  const getOrCreateMasterId = useCallback(async (): Promise<string | null> => {
    const masterTableName = cardForm.cardData?.masterTableName;
    if (!masterTableName || !comId) return null;

    // Eğer masterId zaten biliniyorsa tekrar istek atma
    if (masterId) {
      return masterId;
    }

    try {
      // Varsa oku
      const existing = await referenceService.card.getCardTableData({
        tableName: masterTableName,
        columnName: "COM_ID",
        columnValue: comId,
      });
      const foundId = existing?.data?.[0]?.ID;
      if (foundId) {
        const idStr = foundId.toString();
        setMasterId(idStr);
        return idStr;
      }

      // Yoksa oluştur
      const created = await referenceService.card.post({
        tablename: masterTableName,
        columns: [{ key: "COM_ID", value: comId.toString() }],
      });
      const createdId = (created as any)?.ID ?? (created as any)?.data;
      if (createdId) {
        const idStr = createdId.toString();
        setMasterId(idStr);
        return idStr;
      }
      return null;
    } catch (err) {
      console.error("getOrCreateMasterId error:", err);
      return null;
    }
  }, [cardForm.cardData?.masterTableName, comId, masterId]);

  const fetchDetList = useCallback(
    async (mpId?: string | null): Promise<any[]> => {
      const detailTableName = cardForm.cardData?.tableName;   // MC_PARTOGRAPH_DET
      const detailColumnName = cardForm.cardData?.columnName; // MP_ID
      const _mpId = mpId ?? masterId ?? (await getOrCreateMasterId());
      if (!detailTableName || !detailColumnName || !_mpId) {
        setDetList([]);
        return [];
      }

      try {
        setLoadingDetList(true);
        const resp = await referenceService.card.getCardTableData({
          tableName: detailTableName,
          columnName: detailColumnName,
          columnValue: Number(_mpId),
          mcpdNameList: "PREZENTATION", // MCP alanları
        });
        const rows = (resp?.data ?? []) as any[];
        setDetList(rows);
        return rows;
      } catch (err) {
        console.error("fetchDetList error:", err);
        setDetList([]);
        return [];
      } finally {
        setLoadingDetList(false);
      }
    },
    [cardForm.cardData?.columnName, cardForm.cardData?.tableName, getOrCreateMasterId, masterId],
  );

  const getOrCreateDetId = useCallback(
    async (mpId?: string | null): Promise<string | null> => {
      const detailTableName = cardForm.cardData?.tableName;
      const detailColumnName = cardForm.cardData?.columnName;
      if (!detailTableName || !detailColumnName) return null;

      // 1) Elimizde zaten bir DET_ID varsa direkt onu dön
      if (detId) {
        return detId;
      }

      const _mpId = mpId ?? masterId ?? (await getOrCreateMasterId());
      if (!_mpId) return null;

      try {
        // 2) fetchDetList ile zaten çekilmiş liste varsa, oradan en yeni DET'i bul
        if (detList && detList.length > 0) {
          const newestDet = [...detList].sort(
            (a, b) => Number(b?.ID ?? 0) - Number(a?.ID ?? 0),
          )[0];
          if (newestDet?.ID) {
            const idStr = String(newestDet.ID);
            setDetId(idStr);
            return idStr;
          }
        }

        // 3) Hiç DET yoksa yeni DET oluştur – Doğum Bilgileri alanlarını doldurarak
        const now = getLocalISO();
        const fd: any = cardForm.formData || {};

        const detColumns = [
          { key: detailColumnName, value: _mpId }, // MP_ID
          { key: "PROCESS_TIME", value: now },
          { key: "CTIME", value: now },
          { key: "CUSER", value: user?.username ?? "" },
          { key: "FIRST_TOUCH", value: fd.FIRST_TOUCH ?? "" },
          {
            key: "PAIN_START_TIME",
            value:
              fd.PAIN_START_TIME instanceof Date ? toLocalISO(fd.PAIN_START_TIME) : "",
          },
          {
            key: "BIRTH_TIME",
            value: fd.BIRTH_TIME instanceof Date ? toLocalISO(fd.BIRTH_TIME) : "",
          },
          {
            key: "PLACENTA_EXIT_TIME",
            value:
              fd.PLACENTA_EXIT_TIME instanceof Date
                ? toLocalISO(fd.PLACENTA_EXIT_TIME)
                : "",
          },
          { key: "BABY_HEIGHT", value: fd.BABY_HEIGHT != null ? String(fd.BABY_HEIGHT) : "" },
          { key: "BABY_WEIGHT", value: fd.BABY_WEIGHT != null ? String(fd.BABY_WEIGHT) : "" },
          {
            key: "BABY_HEAD_CIRCUMFERENCE",
            value:
              fd.BABY_HEAD_CIRCUMFERENCE != null
                ? String(fd.BABY_HEAD_CIRCUMFERENCE)
                : "",
          },
          { key: "BABY_GENDER", value: fd.BABY_GENDER ?? "" },
          { key: "PREZENTATION", value: fd.PREZENTATION ?? "" },
          { key: "NOTE", value: fd.NOTE ?? "" },
          { key: "IS_PLACENTA", value: fd.IS_PLACENTA ?? "" },
          { key: "IS_SCA_INDICATION", value: fd.IS_SCA_INDICATION ?? "" },
        ];

        const created = await referenceService.card.post({
          tablename: detailTableName,
          columns: detColumns as { key: string; value: string }[],
        });
        const createdId = (created as any)?.ID ?? (created as any)?.data;
        if (createdId) {
          const idStr = createdId.toString();
          setDetId(idStr);
          // local listeyi de güncelle (fazladan GET atma)
          setDetList((prev) => [
            ...prev,
            {
              ID: createdId,
              MP_ID: Number(_mpId),
              ...detColumns.reduce(
                (acc, c) => ({ ...acc, [(c as any).key]: (c as any).value }),
                {} as Record<string, any>,
              ),
            },
          ]);
          return idStr;
        }
        return null;
      } catch (err) {
        console.error("getOrCreateDetId error:", err);
        return null;
      }
    },
    [
      cardForm.cardData?.columnName,
      cardForm.cardData?.tableName,
      cardForm.formData,
      detId,
      detList,
      getOrCreateMasterId,
      masterId,
      user?.username,
    ],
  );

  const fetchTrace = useCallback(
    async (mpdId?: string | null) => {
      const _mpdId = mpdId ?? detId;
      const traceTableName = (cardForm.cardData as any)?.traceTableName || "MC_PARTOGRAPH_TRACE";
      if (!_mpdId || !traceTableName) return;
      try {
        setLoadingTrace(true);
        const resp = await referenceService.card.getCardTableData({
          tableName: traceTableName,
          columnName: "MPD_ID",
          columnValue: Number(_mpdId),
          mcpdNameList: "PAIN_LEVEL,AMNION_FLUID", // MCP alanları
        });
        const data = (resp?.data ?? []) as any as PartographTraceData[];
        setTraceData(data);
        prepareChartData(data as any[]);
      } catch (err) {
        console.error("fetchTrace error:", err);
      } finally {
        setLoadingTrace(false);
      }
    },
    [cardForm.cardData, detId, prepareChartData],
  );

  const addTraceRecord = useCallback(
    async (trace: Partial<PartographTraceData>) => {
      // ... (keeping existing implementation for add)
      const detailTableName = cardForm.cardData?.tableName;
      const detailColumnName = cardForm.cardData?.columnName;
      const traceTableName = (cardForm.cardData as any)?.traceTableName || "MC_PARTOGRAPH_TRACE";

      if (!detailTableName || !detailColumnName || !traceTableName || !comId) {
        return null;
      }

      const _mpId = masterId ?? (await getOrCreateMasterId());
      if (!_mpId) return null;

      const now = getLocalISO();
      let _activeDetId = detId;

      try {
        if (!_activeDetId) {
          const detColumns = [
            { key: detailColumnName, value: _mpId },
            { key: "PROCESS_TIME", value: now },
            { key: "CTIME", value: now },
            { key: "CUSER", value: user?.username ?? "" },
          ];

          const createdDet = await referenceService.card.post({
            tablename: detailTableName,
            columns: detColumns,
          });

          const createdId = (createdDet as any)?.ID ?? (createdDet as any)?.data;
          if (createdId) {
            _activeDetId = createdId.toString();
            setDetId(_activeDetId);
          } else {
            throw new Error("DET kaydı oluşturulamadı");
          }
        }

        const traceColumns = [
          { key: "MPD_ID", value: _activeDetId },
          { key: "CTIME", value: now },
          {
            key: "PROCESS_TIME",
            value: trace.PROCESS_TIME instanceof Date ? toLocalISO(trace.PROCESS_TIME) : now,
          },
          { key: "CUSER", value: user?.username ?? "" },
          { key: "NURSE_ID", value: user?.id ? String(user.id) : "" },
          { key: "FETAL_HEART_RATE", value: trace.FETAL_HEART_RATE != null ? String(trace.FETAL_HEART_RATE) : "" },
          { key: "SERVIKS_SIZE", value: trace.SERVIKS_SIZE != null ? String(trace.SERVIKS_SIZE) : "" },
          { key: "HEAD_LEVEL", value: trace.HEAD_LEVEL != null ? String(trace.HEAD_LEVEL) : "" },
          { key: "PAIN_LEVEL", value: trace.PAIN_LEVEL != null ? String(trace.PAIN_LEVEL) : "" },
          { key: "PAIN_COUNT", value: trace.PAIN_COUNT != null ? String(trace.PAIN_COUNT) : "" },
          { key: "AMNION_FLUID", value: trace.AMNION_FLUID != null ? String(trace.AMNION_FLUID) : "" },
        ];

        await referenceService.card.post({
          tablename: traceTableName,
          columns: traceColumns,
        });

        await fetchTrace(_activeDetId);
        await fetchDetList(_mpId);
        return _activeDetId;
      } catch (err) {
        console.error("addTraceRecord error:", err);
        throw err;
      }
    },
    [cardForm.cardData, detId, fetchDetList, fetchTrace, getOrCreateMasterId, masterId, comId, user?.username, user?.id],
  );

  const updateTraceRecord = useCallback(
    async (traceId: string, trace: any) => {
      const traceTableName = (cardForm.cardData as any)?.traceTableName || "MC_PARTOGRAPH_TRACE";
      if (!traceId || !traceTableName) return;

      const now = getLocalISO();
      const traceColumns = [
        {
          key: "PROCESS_TIME",
          value: trace.PROCESS_TIME instanceof Date ? toLocalISO(trace.PROCESS_TIME) : now,
        },
        { key: "FETAL_HEART_RATE", value: trace.FETAL_HEART_RATE != null ? String(trace.FETAL_HEART_RATE) : "" },
        { key: "SERVIKS_SIZE", value: trace.SERVIKS_SIZE != null ? String(trace.SERVIKS_SIZE) : "" },
        { key: "HEAD_LEVEL", value: trace.HEAD_LEVEL != null ? String(trace.HEAD_LEVEL) : "" },
        { key: "PAIN_LEVEL", value: trace.PAIN_LEVEL != null ? String(trace.PAIN_LEVEL) : "" },
        { key: "PAIN_COUNT", value: trace.PAIN_COUNT != null ? String(trace.PAIN_COUNT) : "" },
        { key: "AMNION_FLUID", value: trace.AMNION_FLUID != null ? String(trace.AMNION_FLUID) : "" },
      ];

      try {
        await referenceService.card.put({
          tablename: traceTableName,
          id: Number(traceId),
          columns: traceColumns,
        });
        await fetchTrace(detId);
      } catch (err) {
        console.error("updateTraceRecord error:", err);
        throw err;
      }
    },
    [cardForm.cardData, detId, fetchTrace]
  );

  const deleteTraceRecord = useCallback(
    async (traceId: string) => {
      const traceTableName = (cardForm.cardData as any)?.traceTableName || "MC_PARTOGRAPH_TRACE";
      if (!traceId || !traceTableName) return;
      try {
        await referenceService.card.delete({
          tablename: traceTableName,
          id: Number(traceId),
        });
        await fetchTrace();
      } catch (err) {
        console.error("deleteTraceRecord error:", err);
        throw err;
      }
    },
    [cardForm.cardData, fetchTrace],
  );

  const saveMasterRecord = useCallback(async () => {
    // Doğum bilgileri kartı (DET tablosu)
    const detailTableName = cardForm.cardData?.tableName;
    if (!detailTableName) return;

    // Önce mevcut detId'yi kullan, yoksa yeni oluştur
    let _detId = detId;
    if (!_detId) {
      _detId = await getOrCreateDetId();
      if (!_detId) return;
    } else {
    }

    // formData içindeki alanları kolona çevir
    const columns = Object.entries(cardForm.formData || {}).map(([key, value]) => {
      if (value instanceof Date) {
        return { key, value: toLocalISO(value) };
      }
      return { key, value: value != null ? String(value) : "" };
    });

    // MP_ID'yi ekle (Master-Detail ilişkisi için gerekli)
    const _mpId = masterId ?? (await getOrCreateMasterId());
    if (_mpId && cardForm.cardData?.columnName) {
      const mpIdCol = columns.find(col => col.key === cardForm.cardData?.columnName);
      if (mpIdCol) {
        // Varsa ve boşsa güncelle
        if (!mpIdCol.value || mpIdCol.value === '0') {
          mpIdCol.value = _mpId;
        }
      } else {
        // Yoksa ekle
        columns.push({ key: cardForm.cardData.columnName, value: _mpId });
      }
    }

    // ID'yi de ekle (UPDATE için gerekli) - ama sadece yoksa
    const hasId = columns.some(col => col.key === "ID");
    if (!hasId) {
      columns.push({ key: "ID", value: String(_detId) });
    } else {
      // ID varsa ama boşsa, değerini güncelle
      const idColumn = columns.find(col => col.key === "ID");
      if (idColumn && (!idColumn.value || idColumn.value === "")) {
        idColumn.value = String(_detId);
      }
    }

    try {
      await referenceService.card.put({
        tablename: detailTableName,
        id: Number(_detId),
        columns,
      });

      // Başarılı kayıt sonrası detId'yi set et (eğer yoksa)
      if (!detId) {
        setDetId(_detId);
      }
    } catch (err) {
      console.error("saveMasterRecord error:", err);
      throw err;
    }
  }, [cardForm.cardData?.tableName, cardForm.cardData?.columnName, cardForm.formData, detId, masterId, getOrCreateDetId, getOrCreateMasterId]);

  const saveDetRecord = useCallback(async () => {
    // Parametreler kartı da DET tablosu üzerinden ilerliyor; burada ayrı bir işlem yok.
    await saveMasterRecord();
  }, [saveMasterRecord]);

  useEffect(() => {
    // COM_ID hazır olduğunda mevcut DET/TRACE verilerini getir
    const init = async () => {
      if (!comId) {
        setInitialLoading(false);
        return;
      }

      try {
        setInitialLoading(true);
        const mpId = await getOrCreateMasterId();
        if (!mpId) {
          setInitialLoading(false);
          return;
        }

        const dets = await fetchDetList(mpId);

        // Trace grafikleri için varsayılan DET: en yeni ID
        const newestDet = [...(dets ?? [])].sort(
          (a, b) => Number(b?.ID ?? 0) - Number(a?.ID ?? 0),
        )[0];

        // Eğer DET kaydı varsa, formData'yı doldur
        if (newestDet) {
          // formData'yı güncelle - her alan için handleChange kullan
          Object.entries({
            FIRST_TOUCH: newestDet.FIRST_TOUCH || '',
            LAST_MENSTRUAL_PERIOD: newestDet.LAST_MENSTRUAL_PERIOD || '',
            PREZENTATION: newestDet.PREZENTATION || '',
            IS_PLACENTA: newestDet.IS_PLACENTA || '',
            PAIN_START_TIME: newestDet.PAIN_START_TIME ? new Date(newestDet.PAIN_START_TIME) : null,
            BIRTH_TIME: newestDet.BIRTH_TIME ? new Date(newestDet.BIRTH_TIME) : null,
            PLACENTA_EXIT_TIME: newestDet.PLACENTA_EXIT_TIME ? new Date(newestDet.PLACENTA_EXIT_TIME) : null,
            IS_SCA_INDICATION: newestDet.IS_SCA_INDICATION || '',
            BABY_GENDER: newestDet.BABY_GENDER || '',
            BABY_WEIGHT: newestDet.BABY_WEIGHT || '',
            BABY_HEIGHT: newestDet.BABY_HEIGHT || '',
            BABY_HEAD_CIRCUMFERENCE: newestDet.BABY_HEAD_CIRCUMFERENCE || '',
            NOTE: newestDet.NOTE || '',
          }).forEach(([key, value]) => {
            cardForm.handleChange(key, value);
          });
        }

        // DET ID'yi belirle: fetchDetList sonucundan al
        if (newestDet?.ID) {
          const mpdId = String(newestDet.ID);
          setDetId(mpdId);
          await fetchTrace(mpdId);
        } else {
          // DET kaydı yok, ilk kaydet'te oluşturulacak
          // detId boş kalacak, saveMasterRecord içinde oluşturulacak
        }
      } finally {
        setInitialLoading(false);
      }
    };
    void init();
    // Burada kasten sadece comId'ye bağımlıyız; getOrCreate* ve fetch* sabit helper gibi kullanılıyor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comId]);

  return {
    ...cardForm,
    detList,
    loadingDetList,
    traceData,
    loadingTrace,
    fetalHeartRateData,
    serviksSizeData,
    headLevelData,
    painLevelData,
    amnionFluidData,
    addTraceRecord,
    updateTraceRecord,
    deleteTraceRecord,
    saveMasterRecord,
    saveDetRecord,
    prepareChartData,
    fetchDetList,
    initialLoading,
  };
};

